import {
  combineReducers,
  configureStore,
  type Middleware,
} from '@reduxjs/toolkit';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import { authSlice } from './slices/authSlice';
import { publicBatteryRetrofitSlice } from './slices/publicBatteryRetrofitSlice';
import { pxeBackendApi } from './slices/pxeBackendApiSlice';
import { solarQuoteSlice } from './slices/solarQuoteSlice';
import { userLocationSlice } from './slices/userLocationSlice';

const rootReducer = combineReducers({
  [pxeBackendApi.reducerPath]: pxeBackendApi.reducer,
  [solarQuoteSlice.reducerPath]: solarQuoteSlice.reducer,
  [userLocationSlice.reducerPath]: userLocationSlice.reducer,
  [authSlice.reducerPath]: authSlice.reducer,
  [publicBatteryRetrofitSlice.reducerPath]: publicBatteryRetrofitSlice.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export const makeReduxStore = (
  preloadedState?: Partial<RootState>,
  testMiddleware?: Middleware
) =>
  configureStore({
    reducer: rootReducer,
    devTools:
      process.env.NODE_ENV === 'production' ? false : { name: 'redux-store' },
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware().concat(
        pxeBackendApi.middleware
      );
      if (testMiddleware) {
        return middleware.concat(testMiddleware);
      }
      return middleware;
    },
    preloadedState,
  });

export const reduxStore = makeReduxStore();

// Infer the type of `store`
export type ReduxAppStore = ReturnType<typeof makeReduxStore>;
// Infer the `AppDispatch` type from the store itself
export type ReduxAppDispatch = ReduxAppStore['dispatch'];

export const useReduxAppDispatch = () => useDispatch<ReduxAppDispatch>();
export const useReduxAppSelector: TypedUseSelectorHook<RootState> = useSelector;
